import {
  $applyNodeReplacement,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
  TextNode,
} from 'lexical';

/**
 * Lexical-нода упоминания контактного лица (`@`-меншн).
 *
 * Аналог `ActivityLexicalMentionNode` для users, но:
 * - идентификатор — uuid (string), потому что контакты не имеют user_id;
 * - визуально отличается цветом (фиолетовый), чтобы пользователь видел разницу
 *   между упоминанием пользователя и контакта.
 *
 * Сериализуется в content комментария как обычный текст; адресная инфа
 * (uuid + позиции) уходит в `meta.contact_mentions` на сервере.
 */
export type SerializedActivityContactMentionNode = Spread<
  {
    contactUuid: string;
    contactLabel: string;
    type: 'activity-contact-mention';
    version: 1;
  },
  SerializedTextNode
>;

export class ActivityLexicalContactMentionNode extends TextNode {
  __contactUuid: string;
  __contactLabel: string;

  static getType(): string {
    return 'activity-contact-mention';
  }

  static clone(node: ActivityLexicalContactMentionNode): ActivityLexicalContactMentionNode {
    return new ActivityLexicalContactMentionNode(
      node.__contactUuid,
      node.__contactLabel,
      node.__text,
      node.__key
    );
  }

  static importJSON(
    serializedNode: SerializedActivityContactMentionNode
  ): ActivityLexicalContactMentionNode {
    return $createActivityLexicalContactMentionNode(
      serializedNode.contactUuid,
      serializedNode.contactLabel
    ).updateFromJSON(serializedNode);
  }

  constructor(contactUuid: string, contactLabel: string, text?: string, key?: NodeKey) {
    super(text ?? contactLabel, key);
    this.__contactUuid = contactUuid;
    this.__contactLabel = contactLabel;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.dataset.contactUuid = this.__contactUuid;
    dom.dataset.contactLabel = this.__contactLabel;
    dom.style.color = 'var(--mantine-color-violet-7)';
    dom.style.background = 'var(--mantine-color-violet-0)';
    dom.style.borderRadius = '6px';
    dom.style.padding = '0 2px';
    dom.style.boxDecorationBreak = 'clone';
    return dom;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const updated = super.updateDOM(prevNode, dom, config);
    if (
      prevNode.__contactUuid !== this.__contactUuid ||
      prevNode.__contactLabel !== this.__contactLabel
    ) {
      dom.dataset.contactUuid = this.__contactUuid;
      dom.dataset.contactLabel = this.__contactLabel;
    }
    return updated;
  }

  exportJSON(): SerializedActivityContactMentionNode {
    return {
      ...super.exportJSON(),
      contactUuid: this.__contactUuid,
      contactLabel: this.__contactLabel,
      type: 'activity-contact-mention',
      version: 1,
    };
  }

  isTextEntity(): true {
    return true;
  }

  canInsertTextBefore(): false {
    return false;
  }

  canInsertTextAfter(): false {
    return false;
  }

  getContactUuid(): string {
    return this.getLatest().__contactUuid;
  }

  getContactLabel(): string {
    return this.getLatest().__contactLabel;
  }
}

export function $createActivityLexicalContactMentionNode(
  contactUuid: string,
  contactLabel: string
): ActivityLexicalContactMentionNode {
  return $applyNodeReplacement(
    new ActivityLexicalContactMentionNode(contactUuid, contactLabel)
  );
}

export function $isActivityLexicalContactMentionNode(
  node: LexicalNode | null | undefined
): node is ActivityLexicalContactMentionNode {
  return node instanceof ActivityLexicalContactMentionNode;
}
